import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ProcessStageDto, ProcessStageStatus, UpdateStageStatusDto, processStagesApi, handleApiError } from '../../services/committeesService';

interface EditProcessStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  stage: ProcessStageDto;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary);
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: var(--background-color);
  color: var(--text-primary);
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-color)20;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: var(--background-color);
  color: var(--text-primary);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-color)20;
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: var(--background-color);
  color: var(--text-primary);
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-color)20;
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--error-color);
  font-size: 14px;
`;

const InfoBox = styled.div`
  background: var(--primary-color)10;
  border: 1px solid var(--primary-color)30;
  border-radius: var(--border-radius-md);
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--text-primary);
`;

const StageName = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const InfoNote = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: var(--primary-color)08;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  color: var(--text-secondary);
`;

const EditProcessStageModal: React.FC<EditProcessStageModalProps> = ({ isOpen, onClose, onSuccess, stage }) => {
  const [status, setStatus] = useState<number>(stage.status);
  const [stageDate, setStageDate] = useState<string>('');
  const [description, setDescription] = useState<string>(stage.description || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStatus(stage.status);
      setDescription(stage.description || '');
      // Format date for input
      if (stage.stageDate) {
        const date = new Date(stage.stageDate);
        setStageDate(date.toISOString().split('T')[0]);
      } else {
        setStageDate(new Date().toISOString().split('T')[0]);
      }
      setError(null);
    }
  }, [isOpen, stage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const updateDto: UpdateStageStatusDto = {
        status,
        stageDate: stageDate || undefined,
        description: description || undefined
      };

      await processStagesApi.updateStatus(stage.id, updateDto);
      onSuccess();
      onClose();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  const getStatusName = (statusValue: number): string => {
    switch (statusValue) {
      case ProcessStageStatus.Pending:
        return 'ממתין';
      case ProcessStageStatus.Current:
        return 'נוכחי';
      case ProcessStageStatus.Completed:
        return 'הושלם';
      default:
        return 'לא ידוע';
    }
  };

  return (
    <ModalOverlay $isOpen={isOpen} onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>עריכת סטטוס שלב</ModalTitle>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <InfoBox>
              <StageName>{stage.stageName}</StageName>
              <div>סטטוס נוכחי: {stage.statusName}</div>
              <InfoNote>💡 עדכון סטטוס השלב יעדכן את סטטוס הלקוח במערכת</InfoNote>
            </InfoBox>

            <FormGroup>
              <Label htmlFor="status">סטטוס חדש *</Label>
              <Select
                id="status"
                value={status}
                onChange={(e) => setStatus(parseInt(e.target.value))}
                required
              >
                <option value={ProcessStageStatus.Pending}>{getStatusName(ProcessStageStatus.Pending)}</option>
                <option value={ProcessStageStatus.Current}>{getStatusName(ProcessStageStatus.Current)}</option>
                <option value={ProcessStageStatus.Completed}>{getStatusName(ProcessStageStatus.Completed)}</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="stageDate">תאריך השלב</Label>
              <Input
                id="stageDate"
                type="date"
                value={stageDate}
                onChange={(e) => setStageDate(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">הערות</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="הערות נוספות על השלב..."
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <Button type="button" onClick={onClose} disabled={loading}>
              ביטול
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'שומר...' : 'שמירה'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditProcessStageModal;
